import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X, Phone, ChevronRight, Instagram, Facebook, Linkedin } from 'lucide-react';
import { BRAND, NAV } from '../data/mock';

const SOCIALS = [
  { Icon: Instagram, label: 'Instagram', href: BRAND.instagram },
  { Icon: Facebook,  label: 'Facebook',  href: BRAND.facebook },
  { Icon: Linkedin,  label: 'LinkedIn',  href: BRAND.linkedin },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const wrapClass = scrolled
    ? 'bg-[#5C1B22] backdrop-blur-md shadow-[0_4px_24px_rgba(0,0,0,0.18)]'
    : 'bg-[#72272D]';

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${wrapClass}`}>
      {/* Subtle gold hairline */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#C9A875]/35 to-transparent" />

      <div className="container-x flex items-center justify-between h-[126px] lg:h-[152px]">
        {/* Logo */}
        <Link to="/" className="flex items-center group">
          <img
            src={BRAND.logo}
            alt="Tarhun Catering"
            className="h-[84px] sm:h-[92px] lg:h-[88px] w-auto object-contain transition-transform duration-300 group-hover:scale-[1.03]"
            style={{ filter: 'brightness(0) invert(1)' }}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((n) => (
            <NavLink
              key={n.href}
              to={n.href}
              end={n.href === '/'}
              className={({ isActive }) =>
                `relative text-[12.5px] tracking-[0.04em] transition-colors duration-200 ${
                  isActive ? 'text-[#C9A875]' : 'text-[#E8E2D0] hover:text-[#C9A875]'
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center">
          {/* Right stack: socials (top) → CTA (middle) → phone (bottom) */}
          <div className="flex flex-col items-end gap-2.5">
            {/* Socials row — above the CTA */}
            <div className="flex items-center gap-4 mb-0.5">
              {SOCIALS.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={label}
                  data-testid={`nav-social-${label.toLowerCase()}`}
                  className="text-[#F5F0E5]/70 hover:text-[#C9A875] transition-colors duration-300"
                >
                  <Icon size={15} strokeWidth={1.6} />
                </a>
              ))}
            </div>

            {/* CTA */}
            <Link
              to="/iletisim"
              data-testid="navbar-quote-cta"
              className="navbar-cta group inline-flex items-center justify-center gap-2.5 bg-[#C9A875] text-[#72272D] px-9 py-[11px] text-[12px] tracking-[0.22em] uppercase font-semibold rounded-sm transition-colors duration-300 hover:bg-[#E0C896]"
            >
              Teklif Al
              <ChevronRight
                size={14}
                className="transition-transform duration-300 group-hover:translate-x-0.5"
              />
            </Link>

            {/* Phone */}
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="flex items-center gap-2.5 text-[15px] font-medium text-[#F5F0E5] hover:text-[#C9A875] transition-colors duration-300"
              style={{ fontFeatureSettings: '"tnum" 1', fontVariantNumeric: 'tabular-nums' }}
            >
              <Phone size={15} className="text-[#C9A875]" strokeWidth={1.7} />
              {BRAND.phone}
            </a>
          </div>
        </div>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden p-2 text-[#F5F0E5]"
          aria-label="menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#5C1B22] border-t border-[#C9A875]/20">
          <div className="container-x py-6 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.href}
                to={n.href}
                end={n.href === '/'}
                className={({ isActive }) =>
                  `py-3 px-2 text-sm border-b border-brand-light/10 ${
                    isActive ? 'text-[#C9A875]' : 'text-[#E8E2D0]'
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-4 flex flex-col gap-3">
              <a
                href={`tel:${BRAND.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 border border-[#C9A875]/55 text-[#C9A875] py-3 text-[12.5px] tracking-[0.18em] uppercase rounded-sm"
              >
                <Phone size={14} /> {BRAND.phone}
              </a>
              <Link
                to="/iletisim"
                className="navbar-cta inline-flex items-center justify-center gap-2 bg-[#C9A875] text-[#72272D] py-3 text-[12.5px] tracking-[0.18em] uppercase font-semibold rounded-sm"
              >
                Ücretsiz Teklif Al <ChevronRight size={14} />
              </Link>

              {/* Mobile socials */}
              <div className="flex items-center justify-center gap-6 pt-5 mt-2 border-t border-[#C9A875]/15">
                {SOCIALS.map(({ Icon, label, href }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="text-[#F5F0E5]/75 hover:text-[#C9A875] transition-colors"
                  >
                    <Icon size={18} strokeWidth={1.6} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Premium diagonal shine — sweeps left → right every 5.5s */}
      <style>{`
        .navbar-cta {
          position: relative;
          overflow: hidden;
          box-shadow: 0 4px 18px rgba(201,168,117,0.22);
          transition: box-shadow 300ms ease, transform 300ms ease;
        }
        .navbar-cta:hover {
          box-shadow: 0 6px 28px rgba(201,168,117,0.45);
        }
        .navbar-cta::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 55%;
          height: 100%;
          background: linear-gradient(
            100deg,
            rgba(255,250,235,0) 0%,
            rgba(255,250,235,0) 30%,
            rgba(255,252,240,0.85) 50%,
            rgba(255,250,235,0) 70%,
            rgba(255,250,235,0) 100%
          );
          transform: translateX(-160%) skewX(-22deg);
          animation: ctaShine 5.5s cubic-bezier(0.55, 0, 0.25, 1) infinite;
          pointer-events: none;
        }
        @keyframes ctaShine {
          0%, 62% {
            transform: translateX(-160%) skewX(-22deg);
          }
          82% {
            transform: translateX(290%) skewX(-22deg);
          }
          100% {
            transform: translateX(290%) skewX(-22deg);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .navbar-cta::after { animation: none; }
        }
      `}</style>
    </header>
  );
};

export default Navbar;
